import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Select,
    Button,
    Typography,
    Alert,
    Spin,
    Tag,
} from "antd";

import {
    DownloadOutlined,
    TeamOutlined,
    FilePdfOutlined,
    CalendarOutlined,
    UserOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";

const {
    Text: AntText,
} = Typography;

/* =========================================================
   IGNITE SKILLS TRAINING CENTER COLORS
========================================================= */

const INK = "#25313C";
const NAVY = "#29465B";
const BLUE = "#3E6B82";
const GOLD = "#D9A441";
const ORANGE = "#C9822B";

const PAGE_BACKGROUND = "#FFFFFF";
const SOFT_BACKGROUND = "#F7F9FB";
const HEADER_BACKGROUND = "#EEF3F6";
const TABLE_HEADER = "#35596D";
const TABLE_HEADER_LIGHT = "#EAF0F3";

const BORDER = "#D9E1E6";
const LIGHT_BORDER = "#E8EDF0";

const TEXT = "#25313C";
const MUTED = "#697782";
const WHITE = "#FFFFFF";

const SUCCESS = "#4F8A5B";
const SOFT_SUCCESS = "#EEF7F0";

/* =========================================================
   PDF STYLES
========================================================= */

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 32,
        paddingBottom: 42,
        paddingHorizontal: 34,
        backgroundColor: PAGE_BACKGROUND,
        fontFamily: "Helvetica",
    },

    /* -----------------------------------------------------
       HEADER
    ----------------------------------------------------- */

    header: {
        paddingVertical: 15,
        paddingHorizontal: 18,
        marginBottom: 15,
        backgroundColor: HEADER_BACKGROUND,
        borderWidth: 1,
        borderColor: BORDER,
        borderBottomWidth: 3,
        borderBottomColor: GOLD,
    },

    schoolName: {
        color: NAVY,
        fontSize: 19,
        fontWeight: "bold",
        textAlign: "center",
        letterSpacing: 0.5,
        marginBottom: 4,
    },

    schoolSubtitle: {
        color: BLUE,
        fontSize: 9,
        fontWeight: "bold",
        textAlign: "center",
        letterSpacing: 1,
    },

    /* -----------------------------------------------------
       REPORT TITLE
    ----------------------------------------------------- */

    titleContainer: {
        marginBottom: 13,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: BORDER,
    },

    title: {
        color: TEXT,
        fontSize: 15,
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: 4,
    },

    className: {
        color: ORANGE,
        fontSize: 12,
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: 3,
    },

    academicYear: {
        color: MUTED,
        fontSize: 9,
        textAlign: "center",
    },

    /* -----------------------------------------------------
       TABLE
    ----------------------------------------------------- */

    table: {
        width: "100%",
        borderWidth: 1,
        borderColor: BORDER,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: TABLE_HEADER,
        borderBottomWidth: 1,
        borderBottomColor: TABLE_HEADER,
    },

    tableRow: {
        flexDirection: "row",
        minHeight: 28,
        borderBottomWidth: 1,
        borderBottomColor: LIGHT_BORDER,
        backgroundColor: WHITE,
    },

    tableRowAlternate: {
        flexDirection: "row",
        minHeight: 28,
        borderBottomWidth: 1,
        borderBottomColor: LIGHT_BORDER,
        backgroundColor: SOFT_BACKGROUND,
    },

    cell: {
        paddingVertical: 6,
        paddingHorizontal: 7,
        fontSize: 9,
        color: TEXT,
        justifyContent: "center",
    },

    headerCell: {
        paddingVertical: 7,
        paddingHorizontal: 7,
        fontSize: 8.5,
        fontWeight: "bold",
        color: WHITE,
        justifyContent: "center",
    },

    numberCell: {
        width: "7%",
        textAlign: "center",
    },

    nameCell: {
        width: "38%",
    },

    genderCell: {
        width: "15%",
        textAlign: "center",
    },

    classCell: {
        width: "22%",
        textAlign: "center",
    },

    statusCell: {
        width: "18%",
        textAlign: "center",
    },

    /* -----------------------------------------------------
       SUMMARY
    ----------------------------------------------------- */

    summaryContainer: {
        marginTop: 14,
        paddingVertical: 10,
        paddingHorizontal: 13,
        backgroundColor: SOFT_SUCCESS,
        borderWidth: 1,
        borderColor: "#D7E9DA",
        borderLeftWidth: 4,
        borderLeftColor: SUCCESS,
    },

    summaryRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    summaryLabel: {
        color: TEXT,
        fontSize: 9,
        fontWeight: "bold",
    },

    summaryValue: {
        color: SUCCESS,
        fontSize: 11,
        fontWeight: "bold",
    },

    /* -----------------------------------------------------
       FOOTER
    ----------------------------------------------------- */

    footer: {
        position: "absolute",
        bottom: 17,
        left: 34,
        right: 34,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: BORDER,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    footerLeft: {
        color: MUTED,
        fontSize: 7.5,
    },

    footerRight: {
        color: MUTED,
        fontSize: 7.5,
        textAlign: "right",
    },
});

/* =========================================================
   PDF DOCUMENT
========================================================= */

const StudentGradeClassPDF = ({
    students,
    gradeClassName,
    academicYearName,
}) => {
    const safeStudents = Array.isArray(students)
        ? students
        : [];

    return (
        <Document
            title={`${gradeClassName || "Grade Class"} Student List`}
            author="PASSION AIDS THE FUTURE"
            subject="Student Admission Record"
        >
            <Page
                size="A4"
                orientation="landscape"
                style={pdfStyles.page}
            >
                {/* -------------------------------------------------
                    REPORT HEADER
                ------------------------------------------------- */}

                <View
                    style={pdfStyles.header}
                    fixed
                >
                    <Text style={pdfStyles.schoolName}>
                        PASSION AIDS THE FUTURE
                    </Text>

                    <Text
                        style={
                            pdfStyles.schoolSubtitle
                        }
                    >
                        STUDENT ADMISSION RECORD
                    </Text>
                </View>

                {/* -------------------------------------------------
                    REPORT TITLE
                ------------------------------------------------- */}

                <View
                    style={
                        pdfStyles.titleContainer
                    }
                >
                    <Text style={pdfStyles.title}>
                        STUDENT ADMISSION RECORD
                    </Text>

                    <Text
                        style={
                            pdfStyles.className
                        }
                    >
                        {gradeClassName ||
                            "Grade Class"}
                    </Text>

                    <Text
                        style={
                            pdfStyles.academicYear
                        }
                    >
                        Academic Year:{" "}
                        {academicYearName ||
                            "N/A"}
                    </Text>
                </View>

                {/* -------------------------------------------------
                    STUDENT TABLE

                    The table is intentionally allowed to
                    continue across pages.

                    The header is fixed so React PDF repeats
                    it on every page.

                    Each row uses wrap={false} so an individual
                    student's record is never cut between pages.
                ------------------------------------------------- */}

                <View style={pdfStyles.table}>
                    <View
                        style={
                            pdfStyles.tableHeader
                        }
                        fixed
                    >
                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.numberCell,
                            ]}
                        >
                            NO.
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.nameCell,
                            ]}
                        >
                            FULL NAME
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.genderCell,
                            ]}
                        >
                            GENDER
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.classCell,
                            ]}
                        >
                            GRADE CLASS
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.statusCell,
                            ]}
                        >
                            STATUS
                        </Text>
                    </View>

                    {safeStudents.map(
                        (student, index) => (
                            <View
                                key={
                                    student?.id ||
                                    `student-${index}`
                                }
                                style={
                                    index % 2 === 0
                                        ? pdfStyles.tableRow
                                        : pdfStyles.tableRowAlternate
                                }
                                wrap={false}
                            >
                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.numberCell,
                                    ]}
                                >
                                    {index + 1}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.nameCell,
                                    ]}
                                >
                                    {student?.full_name ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.genderCell,
                                    ]}
                                >
                                    {student?.gender ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.classCell,
                                    ]}
                                >
                                    {student?.grade_class_name ||
                                        gradeClassName ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.statusCell,
                                    ]}
                                >
                                    {student?.status ||
                                        "N/A"}
                                </Text>
                            </View>
                        )
                    )}
                </View>

                {/* -------------------------------------------------
                    SUMMARY

                    This is NOT fixed and NOT forced onto the
                    first page.

                    If there is not enough space, React PDF
                    automatically moves the complete summary
                    to the next page instead of cutting it.
                ------------------------------------------------- */}

                <View
                    style={
                        pdfStyles.summaryContainer
                    }
                    wrap={false}
                >
                    <View
                        style={
                            pdfStyles.summaryRow
                        }
                    >
                        <Text
                            style={
                                pdfStyles.summaryLabel
                            }
                        >
                            TOTAL STUDENTS
                        </Text>

                        <Text
                            style={
                                pdfStyles.summaryValue
                            }
                        >
                            {safeStudents.length}
                        </Text>
                    </View>
                </View>

                {/* -------------------------------------------------
                    FOOTER

                    Fixed on every page.
                ------------------------------------------------- */}

                <View
                    style={pdfStyles.footer}
                    fixed
                >
                    <Text
                        style={
                            pdfStyles.footerLeft
                        }
                    >
                        PASSION AIDS THE FUTURE
                    </Text>

                    <Text
                        style={
                            pdfStyles.footerRight
                        }
                    >
                        Student Admission Record
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const AdmissionStuListByClass = () => {
    const { selectedYear } =
        useAcademicYear();

    const [students, setStudents] =
        useState([]);

    const [
        selectedGradeClassId,
        setSelectedGradeClassId,
    ] = useState(null);

    const [loading, setLoading] =
        useState(false);

    const [downloading, setDownloading] =
        useState(false);

    const [error, setError] =
        useState("");

    const getToken = () => {
        return localStorage.getItem(
            "access"
        );
    };

    const normalizeStudentsResponse = (
        data
    ) => {
        if (Array.isArray(data)) {
            return data;
        }

        if (
            Array.isArray(
                data?.results
            )
        ) {
            return data.results;
        }

        if (
            Array.isArray(
                data?.students
            )
        ) {
            return data.students;
        }

        if (
            Array.isArray(data?.data)
        ) {
            return data.data;
        }

        return [];
    };

    const fetchStudents = async () => {
        const token = getToken();

        if (!token) {
            window.location.href =
                "/login";

            return;
        }

        if (!selectedYear?.id) {
            setStudents([]);
            return;
        }

        try {
            setLoading(true);
            setError("");

            const response =
                await api.get(
                    `/api/users/students/?academic_year=${selectedYear.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

            const normalizedStudents =
                normalizeStudentsResponse(
                    response.data
                );

            setStudents(
                normalizedStudents
            );

            setSelectedGradeClassId(
                null
            );
        } catch (err) {
            console.error(
                "Error loading students:",
                err
            );

            if (
                err.response?.status ===
                401
            ) {
                localStorage.removeItem(
                    "access"
                );

                window.location.href =
                    "/login";

                return;
            }

            setStudents([]);

            setError(
                err.response?.data
                    ?.detail ||
                    "Unable to load students."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectedGradeClassId(
            null
        );

        setStudents([]);

        if (selectedYear?.id) {
            fetchStudents();
        }
    }, [selectedYear]);

    /* =========================================================
       GRADE CLASS OPTIONS
    ========================================================= */

    const gradeClassOptions =
        useMemo(() => {
            const classesMap =
                new Map();

            const safeStudents =
                Array.isArray(students)
                    ? students
                    : [];

            safeStudents.forEach(
                (student) => {
                    if (
                        student?.grade_class ===
                            null ||
                        student?.grade_class ===
                            undefined
                    ) {
                        return;
                    }

                    const classId =
                        student.grade_class;

                    const className =
                        student.grade_class_name ||
                        `Grade Class ${classId}`;

                    if (
                        !classesMap.has(
                            String(
                                classId
                            )
                        )
                    ) {
                        classesMap.set(
                            String(
                                classId
                            ),
                            {
                                value: classId,
                                label: className,
                                searchText:
                                    String(
                                        className
                                    ).toLowerCase(),
                            }
                        );
                    }
                }
            );

            return Array.from(
                classesMap.values()
            ).sort((a, b) =>
                a.label.localeCompare(
                    b.label,
                    undefined,
                    {
                        numeric: true,
                        sensitivity:
                            "base",
                    }
                )
            );
        }, [students]);

    /* =========================================================
       SELECTED GRADE CLASS
    ========================================================= */

    const selectedGradeClass =
        useMemo(() => {
            if (
                selectedGradeClassId ===
                    null ||
                selectedGradeClassId ===
                    undefined
            ) {
                return null;
            }

            const matchingStudent =
                students.find(
                    (student) =>
                        String(
                            student.grade_class
                        ) ===
                        String(
                            selectedGradeClassId
                        )
                );

            if (!matchingStudent) {
                return null;
            }

            return {
                id:
                    matchingStudent.grade_class,

                name:
                    matchingStudent.grade_class_name ||
                    `Grade Class ${matchingStudent.grade_class}`,
            };
        }, [
            students,
            selectedGradeClassId,
        ]);

    /* =========================================================
       STUDENTS IN SELECTED CLASS
    ========================================================= */

    const studentsInSelectedGrade =
        useMemo(() => {
            if (
                selectedGradeClassId ===
                    null ||
                selectedGradeClassId ===
                    undefined
            ) {
                return [];
            }

            return students.filter(
                (student) =>
                    String(
                        student?.grade_class
                    ) ===
                    String(
                        selectedGradeClassId
                    )
            );
        }, [
            students,
            selectedGradeClassId,
        ]);

    /* =========================================================
       DOWNLOAD PDF
    ========================================================= */

    const handleDownloadPDF =
        async () => {
            if (
                selectedGradeClassId ===
                    null ||
                selectedGradeClassId ===
                    undefined
            ) {
                setError(
                    "Please select a Grade Class first."
                );

                return;
            }

            const safeStudents =
                Array.isArray(
                    studentsInSelectedGrade
                )
                    ? studentsInSelectedGrade
                    : [];

            if (
                safeStudents.length ===
                0
            ) {
                setError(
                    "No students were found in the selected Grade Class."
                );

                return;
            }

            try {
                setDownloading(true);
                setError("");

                const gradeClassName =
                    selectedGradeClass?.name ||
                    "Grade Class";

                const blob =
                    await pdf(
                        <StudentGradeClassPDF
                            students={
                                safeStudents
                            }
                            gradeClassName={
                                gradeClassName
                            }
                            academicYearName={
                                selectedYear?.name
                            }
                        />
                    ).toBlob();

                const url =
                    URL.createObjectURL(
                        blob
                    );

                const link =
                    document.createElement(
                        "a"
                    );

                link.href = url;

                link.download =
                    `${gradeClassName
                        .replace(
                            /[^a-z0-9]/gi,
                            "_"
                        )
                        .replace(
                            /_+/g,
                            "_"
                        )}_Students.pdf`;

                document.body.appendChild(
                    link
                );

                link.click();

                document.body.removeChild(
                    link
                );

                URL.revokeObjectURL(
                    url
                );
            } catch (err) {
                console.error(
                    "PDF generation error:",
                    err
                );

                setError(
                    "Unable to generate the PDF. Please try again."
                );
            } finally {
                setDownloading(
                    false
                );
            }
        };

    /* =========================================================
       PAGE
    ========================================================= */

    return (
        <div className="admission-grade-download">
            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <div className="admission-grade-download-header">
                <div className="admission-grade-download-icon">
                    <FilePdfOutlined />
                </div>

                <div className="admission-grade-download-heading">
                    <AntText className="admission-grade-eyebrow">
                        Passion Aids The Future • STUDENT REPORTS
                    </AntText>

                    <div className="admission-grade-title">
                        Download by Grade Class
                    </div>

                    <AntText className="admission-grade-description">
                        Generate a complete student
                        admission list for any grade
                        class in the selected academic
                        year.
                    </AntText>
                </div>

                <div className="admission-grade-year">
                    <CalendarOutlined />

                    <div>
                        <span>
                            Academic Year
                        </span>

                        <strong>
                            {selectedYear?.name ||
                                "Not selected"}
                        </strong>
                    </div>
                </div>
            </div>

            {/* =================================================
                CONTENT
            ================================================= */}

            <div className="admission-grade-download-content">
                {error && (
                    <Alert
                        type="error"
                        showIcon
                        closable
                        message={error}
                        onClose={() =>
                            setError("")
                        }
                        className="admission-grade-alert"
                    />
                )}

                {loading ? (
                    <div className="admission-grade-loading">
                        <Spin />

                        <AntText>
                            Loading grade classes...
                        </AntText>
                    </div>
                ) : (
                    <>
                        {/* =========================================
                            GRADE CLASS SELECTOR
                        ========================================= */}

                        <div className="admission-grade-selector">
                            <label>
                                Select Grade Class
                            </label>

                            <Select
                                showSearch
                                allowClear
                                size="large"
                                placeholder="Search and select a Grade Class"
                                prefix={
                                    <TeamOutlined
                                        style={{
                                            color: ORANGE,
                                        }}
                                    />
                                }
                                value={
                                    selectedGradeClassId
                                }
                                options={
                                    gradeClassOptions
                                }
                                style={{
                                    width: "100%",
                                }}
                                filterOption={(
                                    input,
                                    option
                                ) =>
                                    option?.searchText?.includes(
                                        input.toLowerCase()
                                    )
                                }
                                onChange={(
                                    value
                                ) => {
                                    setSelectedGradeClassId(
                                        value ??
                                            null
                                    );

                                    setError(
                                        ""
                                    );
                                }}
                                notFoundContent={
                                    <AntText
                                        style={{
                                            color: MUTED,
                                        }}
                                    >
                                        No Grade
                                        Classes
                                        found
                                    </AntText>
                                }
                            />
                        </div>

                        {/* =========================================
                            NO STUDENTS
                        ========================================= */}

                        {students.length ===
                            0 &&
                            !error && (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="No students found"
                                    description={
                                        selectedYear?.id
                                            ? "There are no students available for the selected academic year."
                                            : "Please select an academic year."
                                    }
                                    className="admission-grade-alert"
                                />
                            )}

                        {/* =========================================
                            SELECTED CLASS INFORMATION
                        ========================================= */}

                        {selectedGradeClass && (
                            <div className="admission-selected-class">
                                <div className="admission-selected-class-main">
                                    <div className="admission-selected-class-icon">
                                        <TeamOutlined />
                                    </div>

                                    <div>
                                        <span>
                                            Selected
                                            Grade
                                            Class
                                        </span>

                                        <strong>
                                            {
                                                selectedGradeClass.name
                                            }
                                        </strong>
                                    </div>
                                </div>

                                <div className="admission-selected-year">
                                    <span>
                                        Academic
                                        Year
                                    </span>

                                    <strong>
                                        {selectedYear?.name ||
                                            "N/A"}
                                    </strong>
                                </div>

                                <Tag className="admission-student-count">
                                    <UserOutlined />

                                    <span>
                                        {
                                            studentsInSelectedGrade.length
                                        }{" "}
                                        Students
                                    </span>
                                </Tag>
                            </div>
                        )}

                        {/* =========================================
                            DOWNLOAD ACTION
                        ========================================= */}

                        <div className="admission-download-action">
                            <Button
                                type="primary"
                                size="large"
                                icon={
                                    <DownloadOutlined />
                                }
                                loading={
                                    downloading
                                }
                                disabled={
                                    !selectedGradeClassId ||
                                    studentsInSelectedGrade.length ===
                                        0
                                }
                                onClick={
                                    handleDownloadPDF
                                }
                            >
                                {downloading
                                    ? "Generating PDF..."
                                    : "Download Grade Class PDF"}
                            </Button>

                            {selectedGradeClass &&
                                studentsInSelectedGrade.length >
                                    0 && (
                                    <div className="admission-download-ready">
                                        <CheckCircleOutlined />

                                        <span>
                                            {
                                                studentsInSelectedGrade.length
                                            }{" "}
                                            student
                                            records
                                            ready
                                            for
                                            download
                                        </span>
                                    </div>
                                )}
                        </div>
                    </>
                )}
            </div>

            {/* =================================================
                PAGE STYLING
            ================================================= */}

            <style>
                {`
                    .admission-grade-download {
                        overflow: hidden;
                        border-radius: 16px;
                        background: #FFFFFF;
                        border: 1px solid #DDE5EA;
                        box-shadow:
                            0 7px 24px rgba(37,49,60,.055);
                    }

                    .admission-grade-download-header {
                        min-height: 94px;
                        padding: 16px 20px;
                        display: flex;
                        align-items: center;
                        gap: 13px;
                        background:
                            linear-gradient(
                                120deg,
                                #F5F8FA 0%,
                                #FFFFFF 58%,
                                #FBF8F2 100%
                            );
                        border-bottom: 1px solid #DDE5EA;
                    }

                    .admission-grade-download-icon {
                        width: 46px;
                        height: 46px;
                        min-width: 46px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 11px;
                        background: #EEF3F6;
                        color: #35596D;
                        border: 1px solid #D4E0E6;
                        font-size: 21px;
                    }

                    .admission-grade-download-heading {
                        min-width: 0;
                        flex: 1;
                    }

                    .admission-grade-eyebrow {
                        display: block;
                        color: #C9822B;
                        font-size: 8.5px;
                        line-height: 1.1;
                        font-weight: 800;
                        letter-spacing: 1px;
                        text-transform: uppercase;
                        margin-bottom: 4px;
                    }

                    .admission-grade-title {
                        color: #25313C;
                        font-size: 18px;
                        line-height: 1.2;
                        font-weight: 800;
                    }

                    .admission-grade-description {
                        display: block;
                        margin-top: 4px;
                        color: #697782;
                        font-size: 11px;
                        line-height: 1.45;
                    }

                    .admission-grade-year {
                        min-width: 145px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 9px 11px;
                        border-radius: 9px;
                        background: #F7F9FB;
                        color: #35596D;
                        border: 1px solid #DDE5EA;
                    }

                    .admission-grade-year > div {
                        min-width: 0;
                    }

                    .admission-grade-year span {
                        display: block;
                        color: #697782;
                        font-size: 8px;
                        line-height: 1.2;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: .6px;
                        margin-bottom: 2px;
                    }

                    .admission-grade-year strong {
                        display: block;
                        color: #25313C;
                        font-size: 11.5px;
                    }

                    .admission-grade-download-content {
                        padding: 17px 20px 19px;
                    }

                    .admission-grade-alert {
                        margin-bottom: 13px;
                        border-radius: 9px;
                    }

                    .admission-grade-selector label {
                        display: block;
                        margin-bottom: 6px;
                        color: #25313C;
                        font-size: 11px;
                        font-weight: 800;
                    }

                    .admission-grade-selector .ant-select-selector {
                        border-radius: 9px !important;
                        min-height: 43px !important;
                        border-color: #D7E0E5 !important;
                        box-shadow: none !important;
                    }

                    .admission-grade-selector .ant-select-focused .ant-select-selector {
                        border-color: #5E8091 !important;
                        box-shadow:
                            0 0 0 2px rgba(62,107,130,.08) !important;
                    }

                    .admission-selected-class {
                        margin-top: 13px;
                        padding: 11px 13px;
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        flex-wrap: wrap;
                        border-radius: 10px;
                        background: #F6F9FA;
                        border: 1px solid #DDE6EA;
                    }

                    .admission-selected-class-main {
                        display: flex;
                        align-items: center;
                        gap: 9px;
                        flex: 1;
                        min-width: 190px;
                    }

                    .admission-selected-class-icon {
                        width: 34px;
                        height: 34px;
                        min-width: 34px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 8px;
                        background: #FFFFFF;
                        border: 1px solid #D8E2E7;
                        color: #35596D;
                    }

                    .admission-selected-class-main span,
                    .admission-selected-year span {
                        display: block;
                        color: #697782;
                        font-size: 8px;
                        line-height: 1.2;
                        text-transform: uppercase;
                        letter-spacing: .6px;
                        margin-bottom: 3px;
                    }

                    .admission-selected-class-main strong {
                        display: block;
                        color: #25313C;
                        font-size: 13px;
                    }

                    .admission-selected-year {
                        min-width: 125px;
                    }

                    .admission-selected-year strong {
                        color: #25313C;
                        font-size: 12px;
                    }

                    .admission-student-count {
                        margin: 0;
                        height: 31px;
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        border: 1px solid #D9E8DC;
                        border-radius: 8px;
                        background: #F2F8F3;
                        color: #4F8A5B;
                        font-weight: 800;
                    }

                    .admission-download-action {
                        margin-top: 14px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .admission-download-action .ant-btn {
                        height: 42px;
                        padding: 0 17px;
                        border-radius: 8px;
                        background: #35596D;
                        border-color: #35596D;
                        font-weight: 750;
                        box-shadow:
                            0 4px 12px rgba(53,89,109,.14);
                    }

                    .admission-download-action .ant-btn:hover {
                        background: #294B5D !important;
                        border-color: #294B5D !important;
                    }

                    .admission-download-action .ant-btn[disabled] {
                        background: #D9E0E4 !important;
                        border-color: #D9E0E4 !important;
                        color: #89949C !important;
                        box-shadow: none;
                    }

                    .admission-download-ready {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        color: #4F8A5B;
                        font-size: 10px;
                        font-weight: 700;
                    }

                    .admission-grade-loading {
                        min-height: 100px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 9px;
                        color: #697782;
                        font-size: 11px;
                    }

                    @media (max-width: 700px) {
                        .admission-grade-download-header {
                            align-items: flex-start;
                            flex-wrap: wrap;
                            padding: 15px;
                        }

                        .admission-grade-year {
                            width: 100%;
                            box-sizing: border-box;
                        }

                        .admission-grade-download-content {
                            padding: 14px 15px 16px;
                        }
                    }

                    @media (max-width: 500px) {
                        .admission-grade-download {
                            border-radius: 13px;
                        }

                        .admission-grade-download-header {
                            gap: 10px;
                        }

                        .admission-grade-download-icon {
                            width: 40px;
                            height: 40px;
                            min-width: 40px;
                            font-size: 18px;
                        }

                        .admission-grade-title {
                            font-size: 16px;
                        }

                        .admission-grade-description {
                            font-size: 10.5px;
                        }

                        .admission-selected-class {
                            align-items: flex-start;
                            flex-direction: column;
                            gap: 10px;
                        }

                        .admission-selected-year {
                            min-width: 100%;
                        }

                        .admission-download-action {
                            align-items: stretch;
                            flex-direction: column;
                        }

                        .admission-download-action .ant-btn {
                            width: 100%;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default AdmissionStuListByClass;